import { DetailedHTMLProps, FC, HTMLAttributes } from 'react'
import { cn } from '@bem-react/classname'
import { motion, AnimatePresence } from 'framer-motion'
import { Memo } from '@/hoc/Memo'

import './Loading.scss'

const cnLoading = cn('Loading')

export type LoadingType =
  | 'circle'
  | 'circle-two'
  | 'ellips'
  | 'line'
  | 'line-2'
  | 'line-3'
export type LoadingPositionType = 'absolute' | 'fixed'

export interface LoadingSchema
  extends DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  color?: string
  background?: string
  type?: string
  isHeight?: boolean
  isBackground?: boolean
  position?: LoadingPositionType
  visible?: boolean
}

const variants = {
  visible: { opacity: 1 },
  hidden: { opacity: 0 },
}

export const Loading: FC<LoadingSchema> = Memo(
  ({
    color = '#05A4E1',
    background = '#fff',
    type,
    isHeight,
    isBackground,
    className,
    position,
    visible = false,
  }: LoadingSchema): JSX.Element => {
    const getTypeLoader = () => {
      switch (type) {
        case 'circle':
          return (
            <svg
              version='1.1'
              id='loader-1'
              xmlns='http://www.w3.org/2000/svg'
              x='0px'
              y='0px'
              width='40px'
              height='40px'
              viewBox='0 0 40 40'
              enableBackground='new 0 0 40 40'
            >
              <path
                opacity='0.2'
                fill={color}
                d='M20.201,5.169c-8.254,0-14.946,6.692-14.946,14.946c0,8.255,6.692,14.946,14.946,14.946
    s14.946-6.691,14.946-14.946C35.146,11.861,28.455,5.169,20.201,5.169z M20.201,31.749c-6.425,0-11.634-5.208-11.634-11.634
    c0-6.425,5.209-11.634,11.634-11.634c6.425,0,11.633,5.209,11.633,11.634C31.834,26.541,26.626,31.749,20.201,31.749z'
              />
              <path
                fill={color}
                d='M26.013,10.047l1.654-2.866c-2.198-1.272-4.743-2.012-7.466-2.012h0v3.312h0
    C22.32,8.481,24.301,9.057,26.013,10.047z'
              >
                <animateTransform
                  attributeType='xml'
                  attributeName='transform'
                  type='rotate'
                  from='0 20 20'
                  to='360 20 20'
                  dur='0.5s'
                  repeatCount='indefinite'
                />
              </path>
            </svg>
          )
        case 'circle-two':
          return (
            <svg
              width='72'
              height='75'
              viewBox='0 0 72 75'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
              className={cnLoading('circle')}
            >
              <g opacity='0.9'>
                <rect
                  width='69'
                  height='69'
                  transform='translate(0 3)'
                  fill='white'
                />
                <mask
                  id='mask0_5399_43219'
                  maskUnits='userSpaceOnUse'
                  x='34'
                  y='-15'
                  width='70'
                  height='105'
                >
                  <rect
                    width='69'
                    height='103.5'
                    rx='32'
                    transform='matrix(1 0 0 -1 34.5 89.25)'
                    fill='white'
                  />
                </mask>
                <g mask='url(#mask0_5399_43219)'>
                  <circle
                    cx='34.5'
                    cy='37.5'
                    r='34.5'
                    stroke={color}
                    strokeWidth='6'
                  />
                </g>
              </g>
            </svg>
          )
        case 'line-2':
          return (
            <svg
              version='1.1'
              id='Layer_1'
              xmlns='http://www.w3.org/2000/svg'
              x='0px'
              y='0px'
              width='24px'
              height='30px'
              viewBox='0 0 24 30'
            >
              <rect x='0' y='13' width='4' height='5' fill={color}>
                <animate
                  attributeName='height'
                  attributeType='XML'
                  values='5;21;5'
                  begin='0s'
                  dur='0.6s'
                  repeatCount='indefinite'
                />
                <animate
                  attributeName='y'
                  attributeType='XML'
                  values='13; 5; 13'
                  begin='0s'
                  dur='0.6s'
                  repeatCount='indefinite'
                />
              </rect>
              <rect x='10' y='13' width='4' height='5' fill={color}>
                <animate
                  attributeName='height'
                  attributeType='XML'
                  values='5;21;5'
                  begin='0.15s'
                  dur='0.6s'
                  repeatCount='indefinite'
                />
                <animate
                  attributeName='y'
                  attributeType='XML'
                  values='13; 5; 13'
                  begin='0.15s'
                  dur='0.6s'
                  repeatCount='indefinite'
                />
              </rect>
              <rect x='20' y='13' width='4' height='5' fill={color}>
                <animate
                  attributeName='height'
                  attributeType='XML'
                  values='5;21;5'
                  begin='0.3s'
                  dur='0.6s'
                  repeatCount='indefinite'
                />
                <animate
                  attributeName='y'
                  attributeType='XML'
                  values='13; 5; 13'
                  begin='0.3s'
                  dur='0.6s'
                  repeatCount='indefinite'
                />
              </rect>
            </svg>
          )
        case 'line-3':
          return (
            <svg
              version='1.1'
              id='Layer_1'
              xmlns='http://www.w3.org/2000/svg'
              x='0px'
              y='0px'
              width='24px'
              height='30px'
              viewBox='0 0 24 30'
            >
              <rect x='0' y='0' width='4' height='20' fill={color}>
                <animate
                  attributeName='opacity'
                  attributeType='XML'
                  values='1; .2; 1'
                  begin='0s'
                  dur='0.6s'
                  repeatCount='indefinite'
                />
              </rect>
              <rect x='7' y='0' width='4' height='20' fill={color}>
                <animate
                  attributeName='opacity'
                  attributeType='XML'
                  values='1; .2; 1'
                  begin='0.2s'
                  dur='0.6s'
                  repeatCount='indefinite'
                />
              </rect>
              <rect x='14' y='0' width='4' height='20' fill={color}>
                <animate
                  attributeName='opacity'
                  attributeType='XML'
                  values='1; .2; 1'
                  begin='0.4s'
                  dur='0.6s'
                  repeatCount='indefinite'
                />
              </rect>
            </svg>
          )
        case 'ellips':
          return (
            <svg
              version='1.1'
              id='loader-1'
              xmlns='http://www.w3.org/2000/svg'
              x='0px'
              y='0px'
              width='40px'
              height='40px'
              viewBox='0 0 50 50'
            >
              <path
                fill='#000'
                d='M43.935,25.145c0-10.318-8.364-18.683-18.683-18.683c-10.318,0-18.683,8.365-18.683,18.683h4.068c0-8.071,6.543-14.615,14.615-14.615c8.072,0,14.615,6.543,14.615,14.615H43.935z'
              >
                <animateTransform
                  attributeType='xml'
                  attributeName='transform'
                  type='rotate'
                  from='0 25 25'
                  to='360 25 25'
                  dur='0.6s'
                  repeatCount='indefinite'
                />
              </path>
            </svg>
          )
        default:
          return (
            <svg
              version='1.1'
              id='Layer_1'
              xmlns='http://www.w3.org/2000/svg'
              x='0px'
              y='0px'
              width='24px'
              height='30px'
              viewBox='0 0 24 30'
            >
              <rect x='0' y='0' width='4' height='10' fill={color}>
                <animateTransform
                  attributeType='xml'
                  attributeName='transform'
                  type='translate'
                  values='0 0; 0 20; 0 0'
                  begin='0'
                  dur='0.6s'
                  repeatCount='indefinite'
                />
              </rect>
              <rect x='10' y='0' width='4' height='10' fill={color}>
                <animateTransform
                  attributeType='xml'
                  attributeName='transform'
                  type='translate'
                  values='0 0; 0 20; 0 0'
                  begin='0.2s'
                  dur='0.6s'
                  repeatCount='indefinite'
                />
              </rect>
              <rect x='20' y='0' width='4' height='10' fill={color}>
                <animateTransform
                  attributeType='xml'
                  attributeName='transform'
                  type='translate'
                  values='0 0; 0 20; 0 0'
                  begin='0.4s'
                  dur='0.6s'
                  repeatCount='indefinite'
                />
              </rect>
            </svg>
          )
      }
    }
    return (
      <AnimatePresence exitBeforeEnter>
        {visible && (
          <motion.div
            variants={variants}
            initial='hidden'
            animate='visible'
            exit='hidden'
            className={cnLoading(
              {
                height: isHeight,
                absolute: position === 'absolute',
                fixed: position === 'fixed',
              },
              [className],
            )}
            style={{
              background: isBackground ? background : '',
            }}
          >
            {getTypeLoader()}
          </motion.div>
        )}
      </AnimatePresence>
    )
  },
)
